import { useEffect } from 'react';
import Footer from '@/components/Footer';
import { Link } from 'react-router-dom';
import NavBar from '@/components/NavBar';
import SEOHead from '@/components/SEOHead';
import BreadcrumbSEO from '@/components/BreadcrumbSEO';
import ImageUploader from '@/components/ImageUploader';
import { CheckCircle } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const faqItems = [
  {
    question: '¿Cómo comprimir una imagen sin perder calidad?',
    answer: 'Sube tu imagen a nuestra herramienta y selecciona el nivel de compresión deseado. Nuestro algoritmo reduce el tamaño del archivo manteniendo la calidad visual prácticamente idéntica al original.',
  },
  {
    question: '¿Qué formatos de imagen puedo comprimir?',
    answer: 'Puedes comprimir imágenes en formato JPG, PNG, WebP, GIF y más. La herramienta detecta automáticamente el formato y aplica la mejor estrategia de compresión.',
  },
  {
    question: '¿Es seguro comprimir imágenes online?',
    answer: 'Sí. Todo el procesamiento se realiza directamente en tu navegador. Tus imágenes nunca se suben a ningún servidor, garantizando total privacidad y seguridad.',
  },
];

const faqStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

const ComprimirImagen = () => {
  // Inject FAQ JSON-LD with cleanup
  useEffect(() => {
    // Remove stale faq-structured-data-* scripts from other pages
    document.querySelectorAll('script[id^="faq-structured-data-"]').forEach((el) => el.remove());

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-structured-data-comprimir-imagen';
    script.textContent = JSON.stringify(faqStructuredData);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('faq-structured-data-comprimir-imagen');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Comprimir imagen online gratis | Reduce tamaño sin perder calidad"
        description="Comprime imágenes JPG, PNG, WebP online gratis. Reduce el tamaño hasta 90% sin perder calidad. Herramienta rápida, segura y sin registro."
        keywords="comprimir imagen, comprimir imagen online, reducir tamaño imagen, comprimir fotos, comprimir imagen gratis, reducir peso imagen"
        canonical="https://compresorjpeg.com/comprimir-imagen"
      />
      <NavBar />

      <main className="container mx-auto px-4 py-8 max-w-5xl">
        <BreadcrumbSEO
          items={[
            { label: 'Inicio', href: '/' },
            { label: 'Comprimir imagen' },
          ]}
        />

        {/* Hero */}
        <section className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Comprimir imagen online gratis
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-2">
            Reduce el tamaño de tus imágenes hasta un 90% sin perder calidad visible.
            Compatible con JPG, PNG, WebP y más formatos.
          </p>
          <p className="text-sm text-muted-foreground mb-8">
            100% gratis · Sin registro · Procesamiento en tu navegador
          </p>
        </section>

        {/* ImageUploader */}
        <section className="mb-16">
          <ImageUploader />
        </section>

        {/* Benefits */}
        <section className="py-12">
          <h2 className="text-2xl font-bold text-foreground text-center mb-8">
            ¿Por qué usar nuestro compresor de imágenes?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {[
              { title: 'Sin pérdida de calidad', desc: 'Algoritmos inteligentes que mantienen la calidad visual mientras reducen drásticamente el tamaño.' },
              { title: '100% privado y seguro', desc: 'Tus imágenes se procesan en tu navegador. Nunca se suben a servidores externos.' },
              { title: 'Rápido y sin registro', desc: 'Comprime imágenes al instante, sin crear cuenta ni instalar software.' },
              { title: 'Múltiples formatos', desc: 'Compatible con JPG, PNG, WebP, GIF y más formatos de imagen populares.' },
            ].map((item) => (
              <div key={item.title} className="flex gap-3 p-4 rounded-lg border border-border bg-card">
                <CheckCircle className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div>
                  <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* How-to */}
        <section className="py-12">
          <h2 className="text-2xl font-bold text-foreground text-center mb-8">
            Cómo comprimir una imagen en 3 pasos
          </h2>
          <ol className="max-w-2xl mx-auto space-y-6">
            {[
              { step: '1', title: 'Sube tu imagen', desc: 'Arrastra y suelta o selecciona archivos JPG, PNG, WebP desde tu dispositivo.' },
              { step: '2', title: 'Ajusta la compresión', desc: 'Elige el nivel de calidad deseado. Puedes previsualizar el resultado antes de descargar.' },
              { step: '3', title: 'Descarga el resultado', desc: 'Descarga la imagen comprimida con un clic. Sin marcas de agua ni limitaciones.' },
            ].map((item) => (
              <li key={item.step} className="flex gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground font-bold text-sm">
                  {item.step}
                </span>
                <div>
                  <h3 className="font-semibold text-foreground">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* Section 1 — Tool Overview */}
        <section className="py-12 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            La mejor herramienta para comprimir imágenes online
          </h2>
          <p className="text-muted-foreground mb-4">
            CompresorJPEG es un compresor de imágenes gratuito que funciona íntegramente en tu navegador. A diferencia de la mayoría de servicios online, <strong>tus archivos nunca se suben a ningún servidor</strong>. Todo el procesamiento ocurre localmente en tu dispositivo, lo que garantiza privacidad total y una velocidad de compresión que no depende de tu conexión a internet.
          </p>
          <p className="text-muted-foreground mb-4">
            Nuestros algoritmos analizan cada imagen para aplicar la estrategia de compresión más eficiente según su contenido. Fotografías con muchos colores, capturas de pantalla con texto, ilustraciones con áreas planas de color — cada tipo recibe un tratamiento diferente para maximizar la reducción de tamaño sin degradar la calidad visual perceptible.
          </p>
          <p className="text-muted-foreground">
            El resultado es una reducción media del 60-90% en el peso del archivo, manteniendo una calidad que en la mayoría de casos es indistinguible del original a simple vista. Puedes previsualizar el resultado antes de descargar y ajustar el nivel de compresión según tus necesidades específicas.
          </p>
        </section>

        {/* Section 2 — Use Cases */}
        <section className="py-12 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground text-center mb-8">
            Casos de uso: ¿cuándo comprimir imágenes?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="p-5 rounded-lg border border-border bg-card">
              <h3 className="font-semibold text-foreground mb-2">Páginas web y blogs</h3>
              <p className="text-sm text-muted-foreground">
                Las imágenes representan más del 50% del peso de una página web típica. Comprimirlas reduce los tiempos de carga, mejora la experiencia de usuario y disminuye la tasa de rebote. Un sitio que carga en menos de 2 segundos retiene significativamente más visitantes que uno que tarda 5 o más.
              </p>
            </div>
            <div className="p-5 rounded-lg border border-border bg-card">
              <h3 className="font-semibold text-foreground mb-2">Tiendas online y e-commerce</h3>
              <p className="text-sm text-muted-foreground">
                Las tiendas online manejan cientos o miles de fotos de productos. Cada imagen sin optimizar consume ancho de banda y ralentiza la navegación, especialmente en dispositivos móviles donde se realiza más del 60% de las compras online. Comprimir imágenes de producto es esencial para la conversión.
              </p>
            </div>
            <div className="p-5 rounded-lg border border-border bg-card">
              <h3 className="font-semibold text-foreground mb-2">Aplicaciones móviles</h3>
              <p className="text-sm text-muted-foreground">
                Las imágenes dentro de una app afectan el tamaño del paquete de instalación y la velocidad de renderizado. Comprimir los assets gráficos antes de incluirlos en tu app reduce el tamaño del APK o IPA y mejora la fluidez en dispositivos con recursos limitados.
              </p>
            </div>
            <div className="p-5 rounded-lg border border-border bg-card">
              <h3 className="font-semibold text-foreground mb-2">Correo electrónico y documentos</h3>
              <p className="text-sm text-muted-foreground">
                La mayoría de servicios de email limitan los adjuntos a 25MB. Comprimir imágenes antes de adjuntarlas permite enviar más archivos sin superar los límites. También es útil para presentaciones y documentos PDF donde las imágenes pesadas aumentan innecesariamente el tamaño del archivo.
              </p>
            </div>
          </div>
        </section>

        {/* Section 3 — SEO & Core Web Vitals */}
        <section className="py-12 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Compresión de imágenes y SEO: Core Web Vitals
          </h2>
          <p className="text-muted-foreground mb-4">
            Desde 2021, Google utiliza las métricas de <strong>Core Web Vitals</strong> como factor de ranking. Dos de las tres métricas principales están directamente afectadas por el peso de las imágenes:
          </p>
          <ul className="space-y-3 mb-6">
            <li className="flex gap-2 text-muted-foreground">
              <span className="font-semibold text-foreground shrink-0">LCP (Largest Contentful Paint):</span>
              mide cuánto tarda en renderizarse el elemento más grande visible. En la mayoría de páginas, este elemento es una imagen. Una imagen hero de 2MB puede tardar 3-5 segundos solo en descargarse en conexión 4G. El objetivo de Google es menos de 2.5 segundos.
            </li>
            <li className="flex gap-2 text-muted-foreground">
              <span className="font-semibold text-foreground shrink-0">CLS (Cumulative Layout Shift):</span>
              mide los desplazamientos inesperados del contenido. Imágenes sin dimensiones definidas causan saltos de diseño cuando terminan de cargar, penalizando esta métrica.
            </li>
          </ul>
          <p className="text-muted-foreground mb-4">
            <strong>Tamaños recomendados:</strong> una imagen hero no debería superar los 200KB, las imágenes de contenido deberían estar entre 50-100KB, y las miniaturas por debajo de 30KB. Con nuestro compresor puedes alcanzar estos objetivos sin perder calidad visual.
          </p>
          <p className="text-muted-foreground">
            Para una guía completa sobre cómo optimizar imágenes para mejorar tu posicionamiento en Google, consulta nuestro artículo sobre{' '}
            <Link to="/blog/optimizar-imagenes-para-web" className="text-primary underline hover:text-primary/80 transition-colors">
              optimización de imágenes para web y Core Web Vitals
            </Link>.
          </p>
        </section>

        {/* Internal Links */}
        <section className="py-12">
          <h2 className="text-2xl font-bold text-foreground text-center mb-6">
            Herramientas de compresión específicas
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {[
              { slug: 'comprimir-jpg', label: 'Comprimir JPG' },
              { slug: 'comprimir-png', label: 'Comprimir PNG' },
              { slug: 'comprimir-webp', label: 'Comprimir WebP' },
            ].map((link) => (
              <Link
                key={link.slug}
                to={`/herramientas/${link.slug}`}
                className="block p-4 rounded-lg border border-border bg-card text-center hover:shadow-md hover:border-primary/30 transition-all duration-200"
              >
                <span className="font-semibold text-foreground hover:text-primary transition-colors">
                  {link.label}
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* Section 4 — Format Strategy */}
        <section className="py-12 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Formatos de imagen: cuál elegir antes de comprimir
          </h2>
          <p className="text-muted-foreground mb-4">
            No todos los formatos de imagen se comprimen igual. Elegir el formato correcto antes de comprimir puede marcar una diferencia significativa en el resultado final:
          </p>
          <ul className="space-y-3 mb-6">
            <li className="text-muted-foreground">
              <span className="font-semibold text-foreground">JPG/JPEG:</span> ideal para fotografías y imágenes con muchos colores y gradientes. Ofrece excelente compresión con pérdida controlada. Es el formato más universal y compatible.
            </li>
            <li className="text-muted-foreground">
              <span className="font-semibold text-foreground">PNG:</span> perfecto para imágenes que requieren transparencia, capturas de pantalla, logos y gráficos con texto. Soporta compresión sin pérdida, pero genera archivos más pesados que JPG en fotografías.
            </li>
            <li className="text-muted-foreground">
              <span className="font-semibold text-foreground">WebP:</span> el formato moderno desarrollado por Google. Ofrece 25-34% mejor compresión que JPEG con calidad equivalente. Compatible con el 97% de los navegadores actuales, es la mejor opción para uso web en 2026.
            </li>
          </ul>
          <p className="text-muted-foreground">
            Si necesitas convertir entre formatos, prueba nuestro{' '}
            <Link to="/herramientas/convertir-a-webp" className="text-primary underline hover:text-primary/80 transition-colors">
              convertidor a WebP
            </Link>
            . Para una comparativa técnica detallada, lee nuestro artículo{' '}
            <Link to="/blog/jpg-vs-png-vs-webp" className="text-primary underline hover:text-primary/80 transition-colors">
              JPG vs PNG vs WebP
            </Link>.
          </p>
        </section>

        {/* FAQ */}
        <section className="py-12 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-foreground text-center mb-8">
            Preguntas frecuentes
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqItems.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent>
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ComprimirImagen;
